import { fetchAuthSession } from "aws-amplify/auth";
import { EMISSIONS_API_URL } from "../utils/apiConfig";
import { readEmissionsCache, writeEmissionsCache } from "../utils/emissionsCache";
import { useCallback, useEffect, useRef, useState } from "react";
import ecoRepairExample from "../data/EcoRepairScore_Example.json";
import scoringExample from "../data/ScoringExample.json";
import { DASHBOARD_REFRESH_KEY, serializeReportingYear } from '../utils/dashboardRefresh';

function normalizeBenchmarkMetric(metric) {
  if (typeof metric === "number") {
    return { all: metric, scope1_2: null, scope3: null };
  }

  if (!metric || typeof metric !== "object") {
    return { all: null, scope1_2: null, scope3: null };
  }

  return {
    all: metric.all ?? metric.all_scopes ?? null,
    scope1_2: metric.scope1_2 ?? metric.scope_1_2 ?? null,
    scope3: metric.scope3 ?? metric.scope_3 ?? null
  };
}

function normalizeScopeLabel(scope) {
  const value = String(scope ?? "").trim();

  if (!value) {
    return value;
  }

  return /^scope\b/i.test(value) ? value : `Scope ${value}`;
}

function flattenReportingData(reportingData) {
  const rows = [];

  (reportingData || []).forEach((yearEntry) => {
    if (!yearEntry) return;

    const {
      year,
      business_type,
      fte,
      sold_hours,
      revenue_chf,
      energy_cost_chf,
      repair_quota_pct,
      waterbased_paint_pct,
      fleet_electr_pct,
      scopes
    } = yearEntry;

    const commonFields = {
      year,
      business_type,
      fte,
      sold_hours,
      revenue_chf,
      energy_cost_chf,
      repair_quota_pct,
      waterbased_paint_pct,
      fleet_electr_pct
    };

    if (Array.isArray(scopes) && scopes.length > 0) {
      scopes.forEach((scopeEntry) => {
        rows.push({
          ...commonFields,
          scope: normalizeScopeLabel(scopeEntry?.scope),
          // API liefert co2_emissions in kg, UI zeigt in Tonnen an.
          total_emissions:
            scopeEntry?.co2_emissions != null ? scopeEntry.co2_emissions / 1000 : null
        });
      });
    } else {
      rows.push({ ...commonFields, scope: null, total_emissions: null });
    }
  });

  return rows;
}

// Backend "score" keys -> German UI labels for the CO2-Intensität sub-categories (US-29).
// "total" is not a sub-category: it's the overall CO2-Intensität per produktive Stunde (US-28).
const CO2_INTENSITY_CATEGORY_LABELS = {
  heating: "Heizung Gebäude",
  process_heating: "Wärme Prozess",
  electricity: "Strom",
  fleet: "Treibstoff",
  booth: "Heizung Kabine",
  kabine_potential: "Potential Kabine",
  activity: "Betriebsmaterial"
};

const CO2_INTENSITY_OVERALL_KEY = "total";

// Not needed for scoring yet — hide this sub-category until it's ready.
const CO2_INTENSITY_HIDDEN_CATEGORY_KEYS = ["activity"];

// Backend "unit" keys -> display unit (+ sensible decimal precision for that scale).
const CO2_INTENSITY_UNIT_DISPLAY = {
  g_co2e: { unit: "g CO₂e/kWh", unitShort: "g CO₂e/kWh", digits: 0 },
  kg_co2e: { unit: "kg CO₂e", unitShort: "kg CO₂e", digits: 0 },
  renewable_pct: { unit: "%", unitShort: "%", digits: 0 },
  savings_pct: { unit: "%", unitShort: "%", digits: 2 },
  g_co2e_per_km: { unit: "g CO₂e/km", unitShort: "g CO₂e/km", digits: 1 },
  kg_co2e_per_sold_hours: { unit: "kg CO₂e/prod. h", unitShort: "kg CO₂e/prod. h", digits: 3 }
};

function toIntensityMetric(entry) {
  const unitInfo = CO2_INTENSITY_UNIT_DISPLAY[entry?.unit] || null;

  return {
    betrieb: entry?.value ?? entry?.intensity ?? null,
    branchenschnitt: entry?.avg_value_of_all ?? entry?.avg_intensity_of_all ?? null,
    top25: entry?.top25_value_of_all ?? entry?.top25_intensity_of_all ?? null,
    min: entry?.min_value_of_all ?? entry?.min_intensity_of_all ?? null,
    max: entry?.max_value_of_all ?? entry?.max_intensity_of_all ?? null,
    unit: unitInfo?.unit ?? entry?.unit ?? undefined,
    unitShort: unitInfo?.unitShort ?? entry?.unit ?? undefined,
    digits: unitInfo?.digits,
    lowerIsBetter: entry?.direction ? entry.direction === "lower_is_better" : undefined
  };
}

function buildCo2IntensityFromApiScoring(scoringEntries) {
  if (!Array.isArray(scoringEntries) || scoringEntries.length === 0) {
    return null;
  }

  const overallEntry = scoringEntries.find((entry) => entry?.score === CO2_INTENSITY_OVERALL_KEY);
  const categoryEntries = scoringEntries.filter(
    (entry) => entry?.score && entry.score !== CO2_INTENSITY_OVERALL_KEY && !CO2_INTENSITY_HIDDEN_CATEGORY_KEYS.includes(entry.score)
  );

  return {
    overall: overallEntry ? toIntensityMetric(overallEntry) : null,
    categories: categoryEntries.map((entry) => ({
      key: entry.score,
      label: CO2_INTENSITY_CATEGORY_LABELS[entry.score] || entry.score,
      ...toIntensityMetric(entry)
    }))
  };
}

function buildScoringByYear(reportingData) {
  const scoringByYear = {};

  (reportingData || []).forEach((yearEntry) => {
    if (yearEntry?.year == null) return;

    const co2Intensity = buildCo2IntensityFromApiScoring(yearEntry.scoring);

    if (co2Intensity) {
      scoringByYear[yearEntry.year] = { co2Intensity };
    }
  });

  return scoringByYear;
}

function getInitialRows(preloadedData) {
  if (preloadedData?.rows?.length) {
    return preloadedData.rows;
  }

  return readEmissionsCache()?.rows || [];
}

export function useEmissionsData(refreshIntervalSeconds, { preloadedData } = {}) {
  const pendingRefreshRef = useRef(JSON.parse(sessionStorage.getItem(DASHBOARD_REFRESH_KEY) || 'null'));
  const initialRowsRef = useRef(getInitialRows(preloadedData));
  const hasLoadedOnceRef = useRef(initialRowsRef.current.length > 0);

  const shouldShowLoginSkeletonRef = useRef(
    Boolean(pendingRefreshRef.current) || sessionStorage.getItem("greenOneDashboardSkeletonShown") !== "true"
  );

  const [scopes, setScopes] = useState(initialRowsRef.current);
  const [companyName, setCompanyName] = useState(null);
  const [industryAvg, setIndustryAvg] = useState(null);
  const [industryAvgSales, setIndustryAvgSales] = useState(null);
  const [ecoRepairData, setEcoRepairData] = useState(ecoRepairExample);
  const [scoringByYear, setScoringByYear] = useState({});
  const [isLoading, setIsLoading] = useState(shouldShowLoginSkeletonRef.current);
  const [error, setError] = useState("");
  const [retryVersion, setRetryVersion] = useState(0);
  const retry = useCallback(() => setRetryVersion((version) => version + 1), []);

  useEffect(() => {
    let isMounted = true;
    let refreshTimer;
    let refreshAttempts = 0;
    let isFetching = false;

    async function fetchData({ initialLoad = false } = {}) {
      if (isFetching) return;
      isFetching = true;
      let waitingForUpdate = false;
      const shouldShowSkeleton =
        Boolean(pendingRefreshRef.current) || (initialLoad && shouldShowLoginSkeletonRef.current);

      if (shouldShowSkeleton) {
        setIsLoading(true);
      }

      setError("");

      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();

        const res = await fetch(
          EMISSIONS_API_URL,
          {
            cache: "no-store",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (!res.ok) {
          throw new Error(`Die Dashboard-Daten konnten nicht geladen werden (HTTP ${res.status}).`);
        }

        const data = await res.json();

        if (!isMounted) return;
        const pendingRefresh = pendingRefreshRef.current;
        if (pendingRefresh?.previousYearData != null) {
          const currentYearData = serializeReportingYear(data, pendingRefresh.year);
          if (currentYearData === '[]' || currentYearData === pendingRefresh.previousYearData) {
            refreshAttempts += 1;
            if (refreshAttempts >= 40) {
              throw new Error('Die Daten werden noch verarbeitet. Bitte versuche es gleich erneut.');
            }
            waitingForUpdate = true;
            refreshTimer = setTimeout(() => fetchData(), 3000);
            return;
          }
        }

        let parsed;

        try {
          if (Array.isArray(data)) {
            parsed = data;
          } else if (typeof data.body === "string") {
            parsed = JSON.parse(data.body);
          } else if (Array.isArray(data.body)) {
            parsed = data.body;
          } else if (Array.isArray(data?.rows)) {
            parsed = data;
          } else {
            parsed = null;
          }
        } catch {
          parsed = null;
        }

        let rows = [];
        let avg = null;
        let avgSales = null;
        let ecoRepairFromApi = null;
        let apiCompanyName = null;
        let scoringFromApi = {};
        // Nur eine erkannte Antwort darf vorhandene Daten ueberschreiben. Ein
        // unbekanntes Format soll nicht als "keine Daten" interpretiert werden.
        let isRecognizedResponse = true;

        const reportingSource =
          parsed && !Array.isArray(parsed) && Array.isArray(parsed.reporting_data)
            ? parsed
            : Array.isArray(data?.reporting_data)
              ? data
              : null;

        if (reportingSource) {
          rows = flattenReportingData(reportingSource.reporting_data);
          apiCompanyName = reportingSource.companyName || null;
          avg = normalizeBenchmarkMetric(reportingSource.industry_avg);
          avgSales = normalizeBenchmarkMetric(reportingSource.industry_avg_sales);
          scoringFromApi = buildScoringByYear(reportingSource.reporting_data);

          if (reportingSource.ecoRepair) {
            ecoRepairFromApi = reportingSource.ecoRepair;
          }
        } else if (Array.isArray(parsed)) {
          rows = parsed;
        } else if (parsed && Array.isArray(parsed.rows)) {
          rows = parsed.rows;
          avg = normalizeBenchmarkMetric(parsed.industry_avg);
          avgSales = normalizeBenchmarkMetric(parsed.industry_avg_sales);

          if (parsed.ecoRepair) {
            ecoRepairFromApi = parsed.ecoRepair;
          }
        } else if (Array.isArray(data?.rows)) {
          rows = data.rows;
          avg = normalizeBenchmarkMetric(data.industry_avg);
          avgSales = normalizeBenchmarkMetric(data.industry_avg_sales);

          if (data.ecoRepair) {
            ecoRepairFromApi = data.ecoRepair;
          }
        } else {
          isRecognizedResponse = false;
        }

        if (!isMounted) return;

        if (!isRecognizedResponse) {
          console.error("Unbekanntes Antwortformat der Emissions-API:", data);
          if (pendingRefreshRef.current) throw new Error('Die aktualisierten Dashboard-Daten konnten nicht geladen werden.');
          return;
        }

        if (pendingRefreshRef.current && rows.length === 0) {
          throw new Error('Die aktualisierten Dashboard-Daten sind noch nicht verfügbar.');
        }
        pendingRefreshRef.current = null;
        sessionStorage.removeItem(DASHBOARD_REFRESH_KEY);

        setScopes(rows);

        if (rows.length > 0) {
          writeEmissionsCache({ rows });
          hasLoadedOnceRef.current = true;
        } else {
          localStorage.removeItem("emissionsCache");
          hasLoadedOnceRef.current = false;
        }

        if (apiCompanyName) {
          setCompanyName(apiCompanyName);
        }

        setIndustryAvg(avg || normalizeBenchmarkMetric(null));
        setIndustryAvgSales(avgSales || normalizeBenchmarkMetric(null));

        if (ecoRepairFromApi) {
          setEcoRepairData(ecoRepairFromApi);
        }

        // Scoring liefert nur das reporting_data-Format. Bei den anderen Formaten
        // bleibt das bisherige Scoring stehen, ausser es gibt keine Daten mehr.
        if (reportingSource || rows.length === 0) {
          setScoringByYear(scoringFromApi);
        }
      } catch (err) {
        console.error("Fehler beim Laden der Daten:", err);

        if (isMounted) {
          setError(
            err instanceof Error
              ? err.message
              : "Die Dashboard-Daten konnten nicht geladen werden."
          );
        }
      } finally {
        isFetching = false;
        if (isMounted && !waitingForUpdate) {
          if (initialLoad && shouldShowLoginSkeletonRef.current) {
            sessionStorage.setItem("greenOneDashboardSkeletonShown", "true");
            shouldShowLoginSkeletonRef.current = false;
          }

          setIsLoading(false);
        }
      }
    }

    fetchData({ initialLoad: true });

    const intervalMs = Math.max(
      Number(refreshIntervalSeconds || 30) * 1000,
      15000
    );

    const interval = setInterval(() => {
      if (pendingRefreshRef.current) return;
      fetchData({ initialLoad: false });
    }, intervalMs);

    return () => {
      isMounted = false;
      clearTimeout(refreshTimer);
      clearInterval(interval);
    };
  }, [refreshIntervalSeconds, retryVersion]);

  return {
    scopes,
    setScopes,
    companyName,
    industryAvg,
    industryAvgSales,
    ecoRepairData,
    setEcoRepairData,
    scoringByYear,
    scoringFallback: scoringExample,
    isLoading,
    isAwaitingUpdate: Boolean(pendingRefreshRef.current),
    error,
    retry
  };
}
